import { useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, ArrowDownRight, ArrowUpRight, Check, Coins, Loader2, Lock, Minus, Plus, Scale, Target } from 'lucide-react';
import api from '@/lib/api';
import { usePode, useTemModulo } from '@/stores/authStore';
import { useIndicesModal } from '@/stores/indicesModalStore';
import { nomeDaRodada } from './rodadas';
import PedidoRevisao, { type Pedido } from './PedidoRevisao';
import { CABECALHO, CELULA, LINHA, TITULO_COLUNA } from './tabela';
import Preloader from '@/components/Preloader';
import TelaProcessando from '@/components/TelaProcessando';
import LogoFornecedor from '@/components/LogoFornecedor';
import PassosDaCotacao from './PassosDaCotacao';
import SeletorDeRodada from './SeletorDeRodada';
import SecaoCotacaoAnterior from './SecaoCotacaoAnterior';
import type { Situacao } from './situacao';

type Oportunidade = {
  item_id: number;
  ordem: number;
  produto: string;
  embalagem: string | null;
  quantidade_m: number;
  preco: number;
  alvo: number;
  acima_pct: number;
  impacto: number;
  acumulado_pct: number;
};

type FornecedorAnalise = {
  id: number;
  apelido: string;
  nome: string;
  logo_url: string | null;
  excluido: boolean;
  pacote: number;
  falta: number;
  falta_pct: number;
  linhas_vencidas: number;
  valor_vencido: number;
  linhas_nao_cotadas: number;
  linhas_para_80: number;
  oportunidades: Oportunidade[];
};

type Comparacao = { fornecedor: string; preco: number };

type Analise = {
  rodada: { id: number; numero: number; nome: string; data: string | null };
  alvo: number;
  quantidade_total_m: number;
  total_linhas: number;
  fornecedores: FornecedorAnalise[];
  pareto: {
    linhas_para_80: number;
    linhas: { item_id: number; produto: string; quantidade_m: number; valor: number; vencedor: string | null; acumulado_pct: number }[];
    distribuicao: { fornecedor: string; linhas: number; valor: number; valor_pct: number }[];
  };
  divergencias: {
    item_id: number;
    produto: string;
    quantidade_m: number;
    mais_barato: Comparacao;
    mais_caro: Comparacao;
    diferenca_pct: number;
    diferenca_rs: number;
  }[];
  empates: {
    item_id: number;
    produto: string;
    quantidade_m: number;
    vencedor: Comparacao;
    segundo: Comparacao;
    diferenca_pct: number;
    diferenca_rs: number;
    valor_linha: number;
  }[];
  cobre: Cobre | null;
  evolucao: {
    numero: number;
    nome: string;
    alvo: number;
    alvo_variacao_pct: number | null;
    pacotes: { fornecedor: string; pacote: number; variacao_pct: number | null; variacao_rs: number | null }[];
  }[];
  // Onde a resposta de um pedido entra: pedir sobre a última rodada abre a seguinte.
  pedido: { abre_rodada: boolean; rodada_id: number | null; numero: number; nome: string; impedimento: string | null };
};

type ToneladaEmReais = { data_lme: string; data_dolar: string; usd_t: number; dolar: number; brl_t: number };

type Cobre = {
  data_rodada: string;
  na_rodada: ToneladaEmReais;
  hoje: ToneladaEmReais;
  // Nenhum pregão nem dólar novo desde as propostas: não há variação a mostrar.
  mesmo_pregao: boolean;
  semana_antes: (ToneladaEmReais & { variacao: number }) | null;
  variacao: number;
  toneladas: number;
  custo_cobre: number;
  impacto: number;
  participacao_no_alvo: number | null;
  estimado: boolean;
  sem_estimativa: string[];
  fornecedores: {
    id: number;
    apelido: string;
    excluido: boolean;
    pacote: number;
    participacao: number | null;
    pacote_com_cobre_de_hoje: number;
    variacao_esperada: number | null;
  }[];
  linhas: {
    item_id: number;
    produto: string;
    kg_km: number;
    estimado: boolean;
    cobre_metro: number;
    preco_alvo: number | null;
    participacao: number | null;
    impacto: number;
  }[];
};

type Cotacao = {
  id: number;
  numero: number;
  descricao: string;
  status: string;
  rodadas: { id: number; numero: number; propostas: unknown[] }[];
  situacao: Situacao;
  proxima_rodada: { nome: string; impedimento: string | null };
};

const reais = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const reaisCurto = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const preco = new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const inteiro = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });
const pct = (v: number, casas = 2) =>
  `${(v * 100).toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })}%`;

/**
 * Uma cor por fornecedor na divisão do alvo. O terceiro era um cinza que, no
 * tema escuro, se confundia com o primeiro; o violeta não repete cor com
 * significado na tela (verde = melhor, âmbar = atenção, vermelho = acima).
 */
const CORES_DA_DIVISAO = ['var(--color-faixa)', 'var(--color-accent)', '#7c3aed'];

/** Mil e milhão abreviados, para caber no cartão sem perder a ordem de grandeza. */
const compacto = (v: number) =>
  v >= 1_000_000
    ? `R$ ${(v / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} mi`
    : v >= 10_000
      ? `R$ ${(v / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil`
      : reaisCurto.format(v);

/**
 * A equalização vista como argumento de negociação.
 *
 * O Marcelo compra o pacote e negocia contra o alvo — o menor preço de cada item,
 * somado. Esta tela responde, para cada fornecedor, a pergunta que ele leva ao
 * telefone: quanto falta, e em quais itens. Quem está mais perto do alvo vem
 * primeiro, porque é a negociação mais curta.
 */
export default function AnalisePage() {
  const { cotacaoId } = useParams();
  const [parametros] = useSearchParams();
  const navigate = useNavigate();

  const [rodadaEscolhida, setRodadaEscolhida] = useState<number | null>(
    parametros.get('rodada') ? Number(parametros.get('rodada')) : null,
  );
  const [excluidos, setExcluidos] = useState<number[]>(
    (parametros.get('excluir') ?? '').split(',').filter(Boolean).map(Number),
  );
  const [foco, setFoco] = useState<number | null>(null);
  const [descontos, setDescontos] = useState<Record<number, string>>({});
  const [mostrarTodos, setMostrarTodos] = useState(false);
  // Itens marcados para o pedido, por fornecedor. Sem marcação ainda, valem os
  // que explicam 80% da diferença.
  const [selecao, setSelecao] = useState<Record<number, number[]>>({});
  const [pedindoPara, setPedindoPara] = useState<number | null>(null);
  const abrirIndices = useIndicesModal((estado) => estado.abrir);
  const queryClient = useQueryClient();
  const podeVerIndices = usePode('indices.ver');
  const podeFechar = usePode('cotacao.fechar');
  const temIndices = useTemModulo('indices');

  const { data: cotacao } = useQuery({
    queryKey: ['cotacao', cotacaoId],
    queryFn: async () => (await api.get<{ data: Cotacao }>(`/cotacoes/${cotacaoId}`)).data.data,
  });

  const rodadaId = rodadaEscolhida ?? cotacao?.rodadas[cotacao.rodadas.length - 1]?.id ?? null;

  const { data: analise, isFetching } = useQuery({
    queryKey: ['analise', rodadaId, excluidos],
    enabled: rodadaId !== null,
    queryFn: async () => {
      const params = new URLSearchParams();
      excluidos.forEach((id) => params.append('excluir[]', String(id)));
      return (await api.get<{ data: Analise }>(`/rodadas/${rodadaId}/analise?${params.toString()}`)).data.data;
    },
  });

  // Pedir nova proposta registra o pedido e, sobre a última rodada, já abre a
  // seguinte — num clique só. O painel lateral mostra o resultado.
  const pedir = useMutation({
    mutationFn: async ({ fornecedorId, itens }: { fornecedorId: number; itens: number[] }) =>
      (
        await api.post<{ data: Pedido }>(`/rodadas/${rodadaId}/pedidos-revisao`, {
          fornecedor_id: fornecedorId,
          itens,
          excluir: excluidos,
        })
      ).data.data,
    onSuccess: () => {
      // A análise continua na rodada em que o pedido foi feito, mesmo com a nova aberta.
      setRodadaEscolhida((atual) => atual ?? rodadaId);
      queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      queryClient.invalidateQueries({ queryKey: ['propostas'] });
      queryClient.invalidateQueries({ queryKey: ['analise'] });
    },
  });

  // A rodada de negociação abre daqui: é na análise que o comprador decide
  // negociar. Aberta, a tela segue para as propostas da rodada nova.
  const [abrindoRodada, setAbrindoRodada] = useState(false);
  const [erroDaRodada, setErroDaRodada] = useState<string | null>(null);
  const abrirRodada = async () => {
    setAbrindoRodada(true);
    setErroDaRodada(null);
    try {
      const nova = (await api.post<{ data: { id: number } }>(`/cotacoes/${cotacaoId}/rodadas`)).data.data;
      await queryClient.invalidateQueries({ queryKey: ['cotacao', cotacaoId] });
      navigate(`/cotacoes/${cotacaoId}/propostas?rodada=${nova.id}`);
    } catch (falha) {
      const dados = (falha as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data;
      setErroDaRodada(dados?.errors ? Object.values(dados.errors)[0][0] : (dados?.message ?? 'Não foi possível abrir a rodada.'));
    } finally {
      setAbrindoRodada(false);
    }
  };

  if (!cotacao || !analise) {
    return <Preloader telaCheia mensagem="Calculando a análise…" className="py-3xl" />;
  }

  const ativos = analise.fornecedores.filter((f) => !f.excluido);
  const maisPerto = ativos[0];
  const emFoco = analise.fornecedores.find((f) => f.id === foco) ?? maisPerto;
  const oportunidadesVisiveis = mostrarTodos
    ? emFoco?.oportunidades ?? []
    : (emFoco?.oportunidades ?? []).slice(0, Math.max(emFoco?.linhas_para_80 ?? 0, 5));

  const alternar = (id: number) =>
    setExcluidos((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  const marcados = (f: FornecedorAnalise) =>
    selecao[f.id] ?? f.oportunidades.slice(0, Math.max(1, f.linhas_para_80)).map((o) => o.item_id);

  const marcar = (f: FornecedorAnalise, itemId: number) =>
    setSelecao((atual) => {
      const agora = marcados(f);
      return { ...atual, [f.id]: agora.includes(itemId) ? agora.filter((x) => x !== itemId) : [...agora, itemId] };
    });

  const fornecedorDoPedido = analise.fornecedores.find((f) => f.id === pedindoPara);

  const pedirNovaProposta = (f: FornecedorAnalise) => {
    pedir.reset();
    setPedindoPara(f.id);
    pedir.mutate({ fornecedorId: f.id, itens: marcados(f) });
  };

  const erroDoPedido = pedir.error
    ? ((pedir.error as { response?: { data?: { errors?: Record<string, string[]>; message?: string } } }).response?.data ?? null)
    : null;
  const mensagemDoErro = erroDoPedido
    ? erroDoPedido.errors
      ? Object.values(erroDoPedido.errors)[0][0]
      : (erroDoPedido.message ?? 'Não foi possível registrar o pedido.')
    : pedir.isError
      ? 'Não foi possível registrar o pedido.'
      : null;

  return (
    <div className="px-lg py-lg">
      {abrindoRodada && (
        <TelaProcessando
          titulo={`Abrindo a ${cotacao.proxima_rodada.nome.toLowerCase()}…`}
          detalhe="Copiando os preços da rodada atual para os fornecedores."
        />
      )}

      <PassosDaCotacao cotacao={cotacao} atual="analise" rodadaId={rodadaId} />

      <div className="mb-md flex flex-wrap items-start justify-between gap-md">
        <div className="min-w-0">
          <p className="text-[12px] font-bold tracking-wide text-accent uppercase">
            Análise dos resultados · {nomeDaRodada(analise.rodada.numero)}
          </p>
          <h1 className="text-[18px] font-bold text-ink">
            <span className="mr-sm text-accent tabular-nums">Nº {cotacao.numero}</span>
            {cotacao.descricao}
          </h1>
          <p className="text-[13px] text-muted">
            {analise.total_linhas} linhas · {inteiro.format(analise.quantidade_total_m)} metros
          </p>
        </div>

        <div className="flex max-w-[520px] flex-col items-end gap-xs">
          <div className="flex flex-wrap justify-end gap-sm">
            <button
              type="button"
              onClick={abrirRodada}
              disabled={cotacao.proxima_rodada.impedimento !== null || abrindoRodada}
              className="inline-flex shrink-0 items-center gap-xs rounded-md border border-accent bg-surface px-md py-sm text-[13px] font-bold text-accent hover:bg-accent hover:text-white disabled:cursor-not-allowed disabled:border-border disabled:text-muted disabled:hover:bg-surface"
            >
              {cotacao.proxima_rodada.impedimento ? <Lock size={14} /> : <Plus size={15} />}
              Abrir {cotacao.proxima_rodada.nome.toLowerCase()}
            </button>
            {/* Os passos do topo já levam ao fechamento, mas a decisão nasce aqui:
                o botão fica ao lado da nova rodada, as duas saídas da análise. */}
            {(podeFechar || cotacao.status === 'fechada') && (
              <Link
                to={`/cotacoes/${cotacao.id}/fechamento`}
                className="inline-flex shrink-0 items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90"
              >
                <Check size={15} aria-hidden="true" />
                {cotacao.status === 'fechada' ? 'Ver fechamento' : 'Ir para o fechamento'}
              </Link>
            )}
          </div>
          {(erroDaRodada ?? cotacao.proxima_rodada.impedimento) && (
            <p className="text-right text-[11px] text-muted">{erroDaRodada ?? cotacao.proxima_rodada.impedimento}</p>
          )}
        </div>
      </div>

      <div className="mb-md flex flex-wrap items-center justify-between gap-sm">
        <SeletorDeRodada rodadas={cotacao.rodadas} atual={rodadaId} aoEscolher={setRodadaEscolhida} />

        <div className="flex flex-wrap items-center gap-xs text-[12px] text-muted">
          <span>Na análise:</span>
          {analise.fornecedores
            .slice()
            .sort((a, b) => a.apelido.localeCompare(b.apelido))
            .map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => alternar(f.id)}
                aria-pressed={!f.excluido}
                className={[
                  'rounded-full border px-sm py-[2px] font-bold transition-colors',
                  f.excluido ? 'border-border text-muted line-through' : 'border-faixa bg-faixa text-white',
                ].join(' ')}
              >
                {f.apelido}
              </button>
            ))}
          {isFetching && <span className="ml-xs">atualizando…</span>}
        </div>
      </div>

      {/* O alvo e a frase que resume a negociação */}
      <section className="mb-lg overflow-hidden rounded-lg border border-accent/40 bg-surface">
        <div className="flex flex-wrap items-center gap-lg px-lg py-md">
          <div className="flex items-center gap-md">
            <span className="flex size-10 items-center justify-center rounded-full bg-accent/10 text-accent">
              <Target size={20} />
            </span>
            <div>
              <p className="text-[12px] font-bold uppercase tracking-wide text-muted">Pacote alvo</p>
              <p className="text-[24px] font-bold tabular-nums text-accent">{reais.format(analise.alvo)}</p>
              <p className="text-[12px] text-muted">o menor preço de cada item, somado</p>
            </div>
          </div>

          {maisPerto && maisPerto.falta > 0 && (
            <p className="min-w-[260px] flex-1 border-l-2 border-accent/30 pl-md text-[14px] leading-relaxed text-ink">
              A negociação mais curta é com a <b>{maisPerto.apelido}</b>: faltam{' '}
              <b>{pct(maisPerto.falta_pct)}</b> ({reais.format(maisPerto.falta)}) para chegar ao alvo, e{' '}
              <b>
                {maisPerto.linhas_para_80} {maisPerto.linhas_para_80 === 1 ? 'item explica' : 'itens explicam'}
              </b>{' '}
              80% dessa diferença.
            </p>
          )}
        </div>
      </section>

      {analise.cobre && (
        <SecaoCobre cobre={analise.cobre} aoVerHistorico={podeVerIndices ? abrirIndices : null} />
      )}

      {/* A compra anterior como régua. A key zera a escolha ao trocar de rodada. */}
      {temIndices && rodadaId !== null && (
        <SecaoCotacaoAnterior key={rodadaId} rodadaId={rodadaId} excluidos={excluidos} />
      )}

      {/* A. Distância até o alvo */}
      <h2 className="mb-sm text-[14px] font-bold text-ink">Quanto falta para cada um chegar ao alvo</h2>
      <div className="mb-lg grid gap-md lg:grid-cols-3">
        {analise.fornecedores.map((f) => {
          const desconto = descontos[f.id] ?? (f.falta_pct * 100).toFixed(2).replace('.', ',');
          const descontoNumero = Number(desconto.replace(',', '.')) / 100;
          const simulado = f.pacote * (1 - (Number.isFinite(descontoNumero) ? descontoNumero : 0));
          const diferenca = simulado - analise.alvo;
          const selecionado = emFoco?.id === f.id;

          return (
            <section
              key={f.id}
              className={[
                'overflow-hidden rounded-lg border bg-surface',
                f.excluido ? 'opacity-50' : '',
                selecionado ? 'border-accent ring-2 ring-accent/15' : 'border-border',
              ].join(' ')}
            >
              <header className="flex items-center justify-between bg-faixa px-md py-sm">
                <h3 className="inline-flex items-center gap-sm text-[14px] font-bold text-white">
                  <LogoFornecedor logoUrl={f.logo_url} apelido={f.apelido} tamanho="sm" />
                  {f.apelido}
                </h3>
                <span className="text-[12px] tabular-nums text-white/70">{reais.format(f.pacote)}</span>
              </header>

              <div className="p-md">
                {f.excluido ? (
                  <p className="text-[13px] text-muted">Fora da análise: não disputa o melhor preço.</p>
                ) : f.falta <= 0.005 ? (
                  <p className="text-[15px] font-bold text-success">Já está no alvo.</p>
                ) : (
                  <>
                    <p className="text-[12px] text-muted">Falta para o alvo</p>
                    <p className="text-[22px] font-bold tabular-nums text-ink">{reais.format(f.falta)}</p>
                    <p className="text-[13px] text-ink">
                      <b className="tabular-nums">{pct(f.falta_pct)}</b> de desconto no pacote dele
                    </p>
                  </>
                )}

                <dl className="mt-md grid grid-cols-2 gap-sm border-t border-border pt-sm text-[12px]">
                  <div>
                    <dt className="text-muted">Vence</dt>
                    <dd className="font-bold text-ink">
                      {f.linhas_vencidas} {f.linhas_vencidas === 1 ? 'linha' : 'linhas'}
                      <span className="block font-normal text-muted">{compacto(f.valor_vencido)}</span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted">Onde está a diferença</dt>
                    <dd className="font-bold text-ink">
                      {f.linhas_para_80} {f.linhas_para_80 === 1 ? 'item' : 'itens'}
                      <span className="block font-normal text-muted">explicam 80%</span>
                    </dd>
                  </div>
                </dl>

                {podeFechar && cotacao.status !== 'fechada' && !f.excluido && (
                  <Link
                    to={`/cotacoes/${cotacao.id}/fechamento?fornecedor=${f.id}`}
                    className="mt-sm inline-flex text-[12px] font-bold text-accent hover:underline"
                  >
                    Fechar o pacote com a {f.apelido}
                  </Link>
                )}

                {!f.excluido && f.falta > 0.005 && (
                  <div className="mt-sm rounded-md bg-dominant px-sm py-sm">
                    <label className="flex items-center gap-xs text-[12px] text-muted">
                      E se ele der
                      <input
                        inputMode="decimal"
                        value={desconto}
                        onChange={(e) => setDescontos((atual) => ({ ...atual, [f.id]: e.target.value }))}
                        aria-label={`Desconto simulado da ${f.apelido}, em %`}
                        className="w-[64px] rounded border border-border bg-surface px-xs py-[2px] text-right tabular-nums text-ink"
                      />
                      % de desconto?
                    </label>
                    <p className="mt-xs text-[12px] text-ink">
                      Pacote de <b className="tabular-nums">{reais.format(simulado)}</b> —{' '}
                      {/* O desconto sugerido vem com 2 casas; a sobra de centavos num
                          pacote de milhões é empate, não "ainda acima do alvo". */}
                      {Math.abs(diferenca) < analise.alvo * 0.0001 ? (
                        <b className="text-success">empata com o alvo</b>
                      ) : diferenca < 0 ? (
                        <b className="text-success">{reais.format(-diferenca)} abaixo do alvo</b>
                      ) : (
                        <span>ainda {reais.format(diferenca)} acima do alvo</span>
                      )}
                    </p>
                  </div>
                )}

                {!f.excluido && f.oportunidades.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setFoco(f.id);
                      setMostrarTodos(false);
                      document.getElementById('onde-negociar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    aria-pressed={selecionado}
                    className={[
                      'mt-md inline-flex w-full items-center justify-center gap-xs rounded-md px-sm py-sm text-[13px] font-bold transition-colors',
                      selecionado
                        ? 'bg-accent text-white hover:bg-accent/90'
                        : 'border border-accent bg-accent/10 text-accent hover:bg-accent hover:text-white',
                    ].join(' ')}
                  >
                    <ArrowDown size={15} aria-hidden="true" />
                    Ver onde negociar com a {f.apelido}
                  </button>
                )}
              </div>
            </section>
          );
        })}
      </div>

      {/* B. Onde negociar */}
      {emFoco && emFoco.oportunidades.length > 0 && (
        <section id="onde-negociar" className="mb-lg scroll-mt-md overflow-hidden rounded-lg border border-border bg-surface">
          <div className="flex flex-wrap items-center justify-between gap-sm border-b border-border px-md py-sm">
            <div>
              <h2 className="text-[14px] font-bold text-ink">Onde negociar com a {emFoco.apelido}</h2>
              <p className="text-[12px] text-muted">
                Itens em que ela não tem o melhor preço, do que mais pesa para o que menos pesa.{' '}
                {emFoco.linhas_para_80} de {emFoco.oportunidades.length} explicam 80% dos {reais.format(emFoco.falta)}.
              </p>
            </div>
            <div className="flex gap-xs rounded-md bg-surface-subtle p-[3px]" role="group" aria-label="Fornecedor">
              {ativos
                .filter((f) => f.oportunidades.length > 0)
                .map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    aria-pressed={f.id === emFoco.id}
                    onClick={() => {
                      setFoco(f.id);
                      setMostrarTodos(false);
                    }}
                    className={[
                      'rounded px-sm py-[3px] text-[12px]',
                      f.id === emFoco.id ? 'bg-surface font-bold text-ink shadow-sm' : 'text-muted hover:text-ink',
                    ].join(' ')}
                  >
                    {f.apelido}
                  </button>
                ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} w-10 text-center`}>
                    <span className="sr-only">No pedido</span>
                  </th>
                  <th className={`${TITULO_COLUNA} text-left`}>Produto</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Quantidade</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Preço dela</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Preço alvo</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Acima</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Pesa no pacote</th>
                  <th className={`${TITULO_COLUNA} min-w-[160px] text-left`}>Da diferença total</th>
                </tr>
              </thead>
              <tbody>
                {oportunidadesVisiveis.map((o, indice) => {
                  const dentroDos80 = indice < emFoco.linhas_para_80;
                  const anterior = indice === 0 ? 0 : oportunidadesVisiveis[indice - 1].acumulado_pct;

                  return (
                    <tr key={o.item_id} className={LINHA}>
                      <td className={`${CELULA} text-center`}>
                        <input
                          type="checkbox"
                          checked={marcados(emFoco).includes(o.item_id)}
                          onChange={() => marcar(emFoco, o.item_id)}
                          aria-label={`Incluir ${o.produto} no pedido à ${emFoco.apelido}`}
                          className="size-4 accent-[var(--color-accent)]"
                        />
                      </td>
                      <td className={`${CELULA} ${dentroDos80 ? 'border-l-2 border-l-accent' : ''}`}>
                        <span className="block text-ink">{o.produto}</span>
                        {o.embalagem && <span className="text-[11px] text-muted">{o.embalagem}</span>}
                      </td>
                      <td className={`${CELULA} whitespace-nowrap text-right tabular-nums`}>{inteiro.format(o.quantidade_m)} m</td>
                      <td className={`${CELULA} text-right tabular-nums`}>{preco.format(o.preco)}</td>
                      <td className={`${CELULA} text-right font-bold tabular-nums text-ink`}>{preco.format(o.alvo)}</td>
                      <td className={`${CELULA} text-right font-bold tabular-nums text-danger`}>+{pct(o.acima_pct)}</td>
                      <td className={`${CELULA} whitespace-nowrap text-right font-bold tabular-nums text-ink`}>{reais.format(o.impacto)}</td>
                      <td className={CELULA}>
                        <div className="flex items-center gap-xs">
                          <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-surface-subtle" aria-hidden="true">
                            <span className="absolute inset-y-0 left-0 bg-accent/25" style={{ width: `${anterior * 100}%` }} />
                            <span
                              className="absolute inset-y-0 bg-accent"
                              style={{ left: `${anterior * 100}%`, width: `${(o.acumulado_pct - anterior) * 100}%` }}
                            />
                          </div>
                          <span className="w-[42px] text-right text-[11px] tabular-nums text-muted">{pct(o.acumulado_pct, 0)}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {emFoco.oportunidades.length > oportunidadesVisiveis.length && (
            <button
              type="button"
              onClick={() => setMostrarTodos(true)}
              className="w-full border-t border-border px-md py-sm text-[12px] font-bold text-accent hover:bg-dominant"
            >
              Mostrar os outros {emFoco.oportunidades.length - oportunidadesVisiveis.length} itens (os 20% restantes)
            </button>
          )}

          <div className="flex flex-wrap items-center justify-between gap-sm border-t border-border bg-dominant px-md py-sm">
            <div className="min-w-0 flex-1 text-[12px] text-muted">
              <p>
                <b className="text-ink">{marcados(emFoco).length}</b>{' '}
                {marcados(emFoco).length === 1 ? 'item marcado' : 'itens marcados'} para o pedido — o texto mostra o alvo e a %
                acima, sem citar concorrentes.
              </p>
              {analise.pedido.impedimento ? (
                <p className="mt-[2px] flex flex-wrap items-center gap-x-xs font-bold text-warning">
                  <AlertTriangle size={13} aria-hidden="true" />
                  {analise.pedido.impedimento}
                  <Link to={`/cotacoes/${cotacao.id}/equalizacao`} className="text-accent hover:underline">
                    Ver equalização
                  </Link>
                </p>
              ) : (
                <p className="mt-[2px]">
                  {analise.pedido.abre_rodada ? (
                    <>
                      O pedido abre a <b className="text-ink">{analise.pedido.nome.toLowerCase()}</b>, com os preços atuais.
                    </>
                  ) : (
                    <>
                      A resposta entra na <b className="text-ink">{analise.pedido.nome.toLowerCase()}</b>, que já está aberta.
                    </>
                  )}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => pedirNovaProposta(emFoco)}
              disabled={marcados(emFoco).length === 0 || pedir.isPending || analise.pedido.impedimento !== null}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              {pedir.isPending ? <Loader2 size={15} className="animate-spin" /> : <Target size={15} />}
              Pedir nova proposta à {emFoco.apelido}
            </button>
          </div>
        </section>
      )}

      {fornecedorDoPedido && (
        <PedidoRevisao
          aberto={pedindoPara !== null}
          aoFechar={() => {
            setPedindoPara(null);
            pedir.reset();
          }}
          cotacaoId={cotacao.id}
          fornecedor={fornecedorDoPedido.apelido}
          carregando={pedir.isPending}
          erro={mensagemDoErro}
          pedido={pedir.data ?? null}
          alvo={analise.alvo}
          falta={fornecedorDoPedido.falta}
          faltaPct={fornecedorDoPedido.falta_pct}
          itens={pedir.data?.itens ?? marcados(fornecedorDoPedido).length}
          destino={analise.pedido}
        />
      )}


      {/* C e D. Divergências e empates */}
      <div className="mb-lg grid items-start gap-md xl:grid-cols-2">
        <section className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="border-b border-border px-md py-sm">
            <h2 className="text-[14px] font-bold text-ink">Preços mais distantes</h2>
            <p className="text-[12px] text-muted">O mesmo produto, do mais barato ao mais caro — os argumentos mais fortes.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} text-left`}>Produto</th>
                  <th className={`${TITULO_COLUNA} text-left`}>Mais caro</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Acima do mais barato</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Na linha</th>
                </tr>
              </thead>
              <tbody>
                {analise.divergencias.map((d) => (
                  <tr key={d.item_id} className={LINHA}>
                    <td className={CELULA}>
                      <span className="block text-ink">{d.produto}</span>
                      <span className="text-[11px] text-muted">{inteiro.format(d.quantidade_m)} m</span>
                    </td>
                    <td className={`${CELULA} text-ink`}>
                      {d.mais_caro.fornecedor}
                      <span className="block text-[11px] text-muted">mais barato: {d.mais_barato.fornecedor}</span>
                    </td>
                    <td className={`${CELULA} text-right font-bold tabular-nums text-danger`}>+{pct(d.diferenca_pct, 1)}</td>
                    <td className={`${CELULA} whitespace-nowrap text-right tabular-nums`}>{reais.format(d.diferenca_rs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-border bg-surface">
          <div className="border-b border-border px-md py-sm">
            <h2 className="text-[14px] font-bold text-ink">Empates técnicos</h2>
            <p className="text-[12px] text-muted">
              Itens decididos por menos de 0,5% — um ajuste pequeno muda quem vence.
            </p>
          </div>
          {analise.empates.length === 0 ? (
            <p className="px-md py-md text-[13px] text-muted">Nenhum item decidido por tão pouco nesta rodada.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className={CABECALHO}>
                    <th className={`${TITULO_COLUNA} text-left`}>Produto</th>
                    <th className={`${TITULO_COLUNA} text-left`}>Disputa</th>
                    <th className={`${TITULO_COLUNA} text-right`}>Diferença</th>
                    <th className={`${TITULO_COLUNA} text-right`}>Para virar</th>
                  </tr>
                </thead>
                <tbody>
                  {analise.empates.map((e) => (
                    <tr key={e.item_id} className={LINHA}>
                      <td className={CELULA}>
                        <span className="block text-ink">{e.produto}</span>
                        <span className="text-[11px] text-muted">
                          {inteiro.format(e.quantidade_m)} m · {compacto(e.valor_linha)}
                        </span>
                      </td>
                      <td className={`${CELULA} text-ink`}>
                        <b>{e.vencedor.fornecedor}</b> vence
                        <span className="block text-[11px] text-muted">{e.segundo.fornecedor} logo atrás</span>
                      </td>
                      <td className={`${CELULA} text-right tabular-nums`}>{pct(e.diferenca_pct)}</td>
                      <td className={`${CELULA} whitespace-nowrap text-right font-bold tabular-nums text-ink`}>
                        {reais.format(e.diferenca_rs)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      {/* E. Onde está o valor */}
      <section className="mb-lg rounded-lg border border-border bg-surface p-md">
        <h2 className="text-[14px] font-bold text-ink">Como o alvo se divide</h2>
        <p className="mb-md text-[12px] text-muted">
          {analise.pareto.linhas_para_80} de {analise.total_linhas} linhas somam 80% do valor — é nelas que a
          negociação pesa.
        </p>

        <div className="flex h-8 overflow-hidden rounded-md" role="img" aria-label="Divisão do alvo por fornecedor">
          {analise.pareto.distribuicao.map((d, i) => (
            <div
              key={d.fornecedor}
              className="flex items-center justify-center text-[11px] font-bold text-white"
              style={{ width: `${d.valor_pct * 100}%`, background: CORES_DA_DIVISAO[i % CORES_DA_DIVISAO.length] }}
              title={`${d.fornecedor}: ${pct(d.valor_pct, 1)}`}
            >
              {d.valor_pct > 0.08 ? d.fornecedor : ''}
            </div>
          ))}
        </div>

        <dl className="mt-sm flex flex-wrap gap-lg text-[12px]">
          {analise.pareto.distribuicao.map((d, i) => (
            <div key={d.fornecedor} className="flex items-center gap-xs">
              <i
                className="block size-3 rounded-sm"
                style={{ background: CORES_DA_DIVISAO[i % CORES_DA_DIVISAO.length] }}
                aria-hidden="true"
              />
              <dt className="font-bold text-ink">{d.fornecedor}</dt>
              <dd className="text-muted">
                {d.linhas} {d.linhas === 1 ? 'linha' : 'linhas'} · {compacto(d.valor)} ({pct(d.valor_pct, 1)})
              </dd>
            </div>
          ))}
        </dl>
      </section>

      {/* F. Evolução */}
      {analise.evolucao.length > 1 && (
        <section className="mb-lg overflow-hidden rounded-lg border border-border bg-surface">
          <div className="border-b border-border px-md py-sm">
            <h2 className="text-[14px] font-bold text-ink">
              <Scale size={15} className="mr-xs inline" aria-hidden="true" />
              O que cada um mudou nas rodadas
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className={CABECALHO}>
                  <th className={`${TITULO_COLUNA} text-left`}>Rodada</th>
                  <th className={`${TITULO_COLUNA} text-right`}>Alvo</th>
                  {analise.evolucao[0].pacotes.map((p) => (
                    <th key={p.fornecedor} className={`${TITULO_COLUNA} text-right`}>
                      {p.fornecedor}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analise.evolucao.map((r) => (
                  <tr key={r.numero} className={LINHA}>
                    <td className={`${CELULA} text-ink`}>{r.nome}</td>
                    <td className={`${CELULA} text-right`}>
                      <span className="block font-bold tabular-nums text-accent">{reais.format(r.alvo)}</span>
                      <Mudanca valor={r.alvo_variacao_pct} />
                    </td>
                    {r.pacotes.map((p) => (
                      <td key={p.fornecedor} className={`${CELULA} text-right`}>
                        <span className="block tabular-nums text-ink">{reais.format(p.pacote)}</span>
                        <Mudanca valor={p.variacao_pct} rs={p.variacao_rs} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Mudanca({ valor, rs }: { valor: number | null; rs?: number | null }) {
  if (valor === null) return null;
  if (Math.abs(valor) < 0.00005) return <span className="text-[11px] text-muted">manteve</span>;

  return (
    <span className={`text-[11px] font-bold tabular-nums ${valor < 0 ? 'text-success' : 'text-danger'}`}>
      {valor > 0 ? '+' : ''}
      {pct(valor)}
      {rs !== undefined && rs !== null ? ` (${reaisCurto.format(rs)})` : ''}
    </span>
  );
}

const dataCurta = (iso: string) => {
  const [ano, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${ano}`;
};
const dolarTonelada = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
const cotacaoDolar = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 4 });

/**
 * O argumento do cobre: "quando sobe, vocês logo repassam — e agora que baixou?".
 *
 * Compara a tonelada em reais nas propostas com a de hoje. Três situações:
 * - caiu: é o argumento, com o valor a menos no pacote;
 * - subiu: a seção diz com a mesma clareza que não é hora de usar o cobre;
 * - sem pregão novo desde as propostas (propostas de fim de semana, ou do
 *   próprio dia): não há variação a mostrar, e a seção diz quando a
 *   comparação passa a existir.
 * Em todas, a semana antes das propostas entra como contexto: "tu está
 * mantendo o preço do cobre da semana passada".
 */
function SecaoCobre({ cobre, aoVerHistorico }: { cobre: Cobre; aoVerHistorico: (() => void) | null }) {
  const semPregaoNovo = cobre.mesmo_pregao;
  const estavel = !semPregaoNovo && Math.abs(cobre.variacao) < 0.0005;
  const caiu = !semPregaoNovo && !estavel && cobre.variacao < 0;
  const subiu = !semPregaoNovo && !estavel && cobre.variacao > 0;
  const semana = cobre.semana_antes;
  const toneladas = cobre.toneladas.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  const tom = (v: number) => (Math.abs(v) < 0.0005 ? 'text-muted' : v < 0 ? 'text-success' : 'text-danger');
  const fundo = (v: number) => (Math.abs(v) < 0.0005 ? 'bg-dominant' : v < 0 ? 'bg-success/10' : 'bg-danger/10');

  const cotacoes = semPregaoNovo
    ? [
        ...(semana ? [{ rotulo: `Semana anterior · ${dataCurta(semana.data_lme)}`, t: semana }] : []),
        { rotulo: `Nas propostas e hoje · ${dataCurta(cobre.na_rodada.data_lme)}`, t: cobre.na_rodada },
      ]
    : [
        { rotulo: `Nas propostas · ${dataCurta(cobre.na_rodada.data_lme)}`, t: cobre.na_rodada },
        { rotulo: `Hoje · ${dataCurta(cobre.hoje.data_lme)}`, t: cobre.hoje },
      ];

  // O quadro de variação mostra a semana anterior quando ainda não há o que comparar com hoje.
  const variacaoDoQuadro = semPregaoNovo ? (semana?.variacao ?? null) : cobre.variacao;

  return (
    <section className="mb-lg overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex flex-wrap items-center justify-between gap-sm border-b border-border px-md py-sm">
        <h2 className="flex items-center gap-xs text-[14px] font-bold text-ink">
          <Coins size={16} className="text-accent" aria-hidden="true" />
          O cobre desde as propostas
        </h2>
        {aoVerHistorico && (
          <button type="button" onClick={aoVerHistorico} className="text-[12px] font-bold text-accent hover:underline">
            Ver o histórico do cobre
          </button>
        )}
      </div>

      <div className="grid gap-lg px-md py-md lg:grid-cols-[1.2fr_1fr]">
        <div>
          <p className="text-[15px] leading-relaxed text-ink">
            {semPregaoNovo ? (
              <>
                As propostas são de <b>{dataCurta(cobre.data_rodada)}</b> e ainda não houve pregão da LME depois delas — o
                último é de <b>{dataCurta(cobre.na_rodada.data_lme)}</b>, com a tonelada a{' '}
                <b>{reaisCurto.format(cobre.na_rodada.brl_t)}</b>. A comparação com as propostas aparece a partir do próximo
                pregão.
              </>
            ) : estavel ? (
              <>
                Desde as propostas ({dataCurta(cobre.data_rodada)}), a tonelada de cobre ficou{' '}
                <b>praticamente estável</b> em reais ({cobre.variacao > 0 ? '+' : ''}
                {pct(cobre.variacao)}). Não há argumento de cobre para pedir desconto agora.
              </>
            ) : (
              <>
                Desde as propostas ({dataCurta(cobre.data_rodada)}), a tonelada de cobre{' '}
                <b className={caiu ? 'text-success' : 'text-danger'}>
                  {caiu ? 'caiu' : 'subiu'} {pct(Math.abs(cobre.variacao))}
                </b>{' '}
                em reais. O pacote tem cerca de <b>{toneladas} t</b> de cobre{cobre.estimado ? ' (estimado)' : ''} —{' '}
                {caiu ? (
                  <>
                    são <b>{reais.format(Math.abs(cobre.impacto))}</b> a menos só de cobre.
                  </>
                ) : (
                  <>são {reais.format(cobre.impacto)} a mais de cobre. Não é argumento para pedir desconto agora.</>
                )}
              </>
            )}
          </p>

          {(semPregaoNovo || estavel) && (
            <p className="mt-xs text-[13px] leading-relaxed text-ink">
              O pacote tem cerca de <b>{toneladas} t</b> de cobre{cobre.estimado ? ' (estimado)' : ''} —{' '}
              {compacto(cobre.custo_cobre)} ao preço das propostas.
            </p>
          )}

          {semana && (
            <p className="mt-xs text-[13px] leading-relaxed text-ink">
              Na semana antes das propostas, a tonelada{' '}
              {Math.abs(semana.variacao) < 0.0005 ? (
                <b>ficou estável</b>
              ) : (
                <b className={tom(semana.variacao)}>
                  {semana.variacao < 0 ? 'caiu' : 'subiu'} {pct(Math.abs(semana.variacao))}
                </b>
              )}{' '}
              ({reaisCurto.format(semana.brl_t)} em {dataCurta(semana.data_lme).slice(0, 5)} →{' '}
              {reaisCurto.format(cobre.na_rodada.brl_t)} em {dataCurta(cobre.na_rodada.data_lme).slice(0, 5)}).
              {semana.variacao <= -0.0005 &&
                ' Se algum fornecedor ainda calcula com o cobre da semana passada, a proposta não pegou essa queda — vale perguntar.'}
            </p>
          )}

          <dl className="mt-md grid grid-cols-3 gap-sm">
            {cotacoes.map(({ rotulo, t }) => (
              <div key={rotulo} className="rounded-md bg-dominant px-sm py-sm">
                <dt className="text-[11px] text-muted">{rotulo}</dt>
                <dd className="text-[16px] font-bold tabular-nums text-ink">{reaisCurto.format(t.brl_t)}/t</dd>
                <dd className="text-[11px] tabular-nums text-muted">
                  {dolarTonelada.format(t.usd_t)} × {cotacaoDolar.format(t.dolar)}
                </dd>
              </div>
            ))}
            {variacaoDoQuadro !== null && (
              <div className={`rounded-md px-sm py-sm ${fundo(variacaoDoQuadro)}`}>
                <dt className="text-[11px] text-muted">{semPregaoNovo ? 'Variação na semana' : 'Desde as propostas'}</dt>
                <dd className={`flex items-center gap-[2px] text-[16px] font-bold tabular-nums ${tom(variacaoDoQuadro)}`}>
                  {Math.abs(variacaoDoQuadro) < 0.0005 ? (
                    <Minus size={16} aria-hidden="true" />
                  ) : variacaoDoQuadro < 0 ? (
                    <ArrowDownRight size={16} aria-hidden="true" />
                  ) : (
                    <ArrowUpRight size={16} aria-hidden="true" />
                  )}
                  {variacaoDoQuadro > 0 ? '+' : ''}
                  {pct(variacaoDoQuadro)}
                </dd>
                <dd className="text-[11px] tabular-nums text-muted">
                  {semPregaoNovo ? 'antes das propostas' : `${reaisCurto.format(cobre.impacto)} no pacote`}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div>
          {caiu || subiu ? (
            <>
              <p className="mb-xs text-[12px] font-bold text-ink">
                {caiu ? 'Se cada um repassasse a queda do cobre' : 'Se cada um repassasse a alta do cobre'}
              </p>
              <ul className="space-y-xs">
                {cobre.fornecedores
                  .filter((f) => !f.excluido)
                  .map((f) => (
                    <li
                      key={f.id}
                      className="flex flex-wrap items-baseline justify-between gap-x-sm rounded-md border border-border px-sm py-xs text-[12px]"
                    >
                      <span className="font-bold text-ink">{f.apelido}</span>
                      <span className="tabular-nums text-muted">
                        {reaisCurto.format(f.pacote)} → <b className="text-ink">{reaisCurto.format(f.pacote_com_cobre_de_hoje)}</b>
                        {f.variacao_esperada !== null && (
                          <span className={caiu ? 'text-success' : 'text-danger'}>
                            {' '}
                            ({f.variacao_esperada > 0 ? '+' : ''}
                            {pct(f.variacao_esperada)})
                          </span>
                        )}
                      </span>
                      {f.participacao !== null && (
                        <span className="basis-full text-[11px] text-muted">o cobre é {pct(f.participacao, 0)} do pacote</span>
                      )}
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <>
              <p className="mb-xs text-[12px] font-bold text-ink">Quanto do pacote de cada um é cobre</p>
              <ul className="space-y-xs">
                {cobre.fornecedores
                  .filter((f) => !f.excluido && f.participacao !== null)
                  .map((f) => (
                    <li key={f.id} className="rounded-md border border-border px-sm py-xs text-[12px]">
                      <div className="flex items-baseline justify-between gap-sm">
                        <span className="font-bold text-ink">{f.apelido}</span>
                        <span className="tabular-nums text-muted">
                          <b className="text-ink">{pct(f.participacao ?? 0, 0)}</b> de {reaisCurto.format(f.pacote)}
                        </span>
                      </div>
                      <div className="mt-[3px] h-1.5 overflow-hidden rounded-full bg-surface-subtle" aria-hidden="true">
                        <span className="block h-full bg-accent" style={{ width: `${Math.min(1, f.participacao ?? 0) * 100}%` }} />
                      </div>
                    </li>
                  ))}
              </ul>
              <p className="mt-xs text-[11px] text-muted">
                Quando o cobre mexer, cada 1% na tonelada muda cerca de {reaisCurto.format(cobre.custo_cobre / 100)} no pacote.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border-t border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className={CABECALHO}>
              <th className={`${TITULO_COLUNA} text-left`}>Onde o cobre mais pesa</th>
              <th className={`${TITULO_COLUNA} text-right`}>Cobre por km</th>
              <th className={`${TITULO_COLUNA} text-right`}>Cobre por metro</th>
              <th className={`${TITULO_COLUNA} text-right`}>Preço alvo</th>
              <th className={`${TITULO_COLUNA} text-right`}>Do preço</th>
              {(caiu || subiu) && <th className={`${TITULO_COLUNA} text-right`}>Variação no item</th>}
            </tr>
          </thead>
          <tbody>
            {cobre.linhas.slice(0, 6).map((l) => (
              <tr key={l.item_id} className={LINHA}>
                <td className={`${CELULA} text-ink`}>{l.produto}</td>
                <td className={`${CELULA} whitespace-nowrap text-right tabular-nums`}>
                  {l.kg_km.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} kg{l.estimado ? '*' : ''}
                </td>
                <td className={`${CELULA} text-right tabular-nums`}>{preco.format(l.cobre_metro)}</td>
                <td className={`${CELULA} text-right tabular-nums`}>{l.preco_alvo !== null ? preco.format(l.preco_alvo) : '—'}</td>
                <td className={`${CELULA} text-right font-bold tabular-nums text-ink`}>
                  {l.participacao !== null ? pct(l.participacao, 0) : '—'}
                </td>
                {(caiu || subiu) && (
                  <td className={`${CELULA} whitespace-nowrap text-right tabular-nums ${l.impacto < 0 ? 'text-success' : 'text-danger'}`}>
                    {reais.format(l.impacto)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t border-border px-md py-sm text-[11px] leading-snug text-muted">
        {cobre.estimado &&
          '* Peso de cobre estimado pela seção nominal (seção × vias × 8,89 kg/km); o peso real varia alguns por cento conforme o fabricante. '}
        Tonelada em reais = LME à vista × dólar PTAX.
        {cobre.sem_estimativa.length > 0 && ` Fora da conta, sem seção de cobre: ${cobre.sem_estimativa.join(', ')}.`}
      </p>
    </section>
  );
}
